import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import PagedTable from '../../components/sorted-table/PagedTable';
import TransactionsList, { TransactionFilterOption, TransactionFilterState } from '../../components/transaction/TransactionsList';
import { FilterOption } from '../../components/custom-input/FilterDropdown';
import ActionMenu from '../../components/action-buttons/ActionButtons';
import { Loader } from '../../components';
import ConfirmModal from '../user-profile/user-edit/confirm-modal/ConfirmModal';
import { InventoryTransaction, StandardParamsToODataParams } from '../../types/interfaces';
import { InventorySource, TransactionStatusEnum, TRANSACTION_TYPE_LABELS, TransactionTypeEnum } from '../../types/enums';
import { showToast } from '../../services/toast.service';
import { getAccountTransactionsOdata } from '../../api/transactionsApi';
import { cancelOnFarmTransaction } from '../../api/onFarmTransactionApi';
import { getOnFarmAccountTransactionsOdata } from '../../services/onFarmTransactionHistory.service';
import {
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  NAVIGATION_LINK_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
  TRANSACTION_CONSTANTS,
} from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { REACT_APP_ON_FARM_ENABLED } from '../../constants/apiEndpoints';
import { ROUTE_PATHS } from '../../constants/routePaths';
import './transactionHistory.scss';

const defaultFilterPreset: TransactionFilterState = {
  type: '',
  status: '',
  resolution: '',
  createdDatetime: { startDate: null, endDate: null },
  dateWasModified: false,
  searches: [],
};

const sourceOptions = [
  { source: InventorySource.RSG, label: NAVIGATION_LINK_CONSTANTS.RSG_TRANSACTION_HISTORY },
  ...(REACT_APP_ON_FARM_ENABLED
    ? [{ source: InventorySource.ON_FARM, label: NAVIGATION_LINK_CONSTANTS.ON_FARM_TRANSACTION_HISTORY }]
    : []),
];

const TransactionHistory = (): JSX.Element => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const activeAccount = useAppSelector(state => state.activeAccount);

  const [inventorySource, setInventorySource] = useState<InventorySource>(
    REACT_APP_ON_FARM_ENABLED && state?.inventorySource === InventorySource.ON_FARM
      ? InventorySource.ON_FARM
      : InventorySource.RSG,
  );
  const [filter, setFilter] = useState<string>(state?.filter ?? undefined);
  const [filterPreset, setFilterPreset] = useState<TransactionFilterState>(state?.filterPreset ?? undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [transactionsData, setTransactionsData] = useState<InventoryTransaction[]>([]);
  const [listRefresh, setListRefresh] = useState<number>(0);
  const [transactionToCancel, setTransactionToCancel] = useState<InventoryTransaction>();
  const [cancellingTransaction, setCancellingTransaction] = useState<boolean>(false);

  const isOnFarm = inventorySource === InventorySource.ON_FARM;
  const transactionFetchLimit = 20;
  const transactionInclude = isOnFarm ? 'OnFarmSpecimen.OnFarmAnimal' : 'Specimen.Animal';

  const callNumber: MutableRefObject<number> = useRef<number>(0);
  const hasAllResults: MutableRefObject<boolean> = useRef<boolean>(false);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef<boolean>(false);
  const ref = useRef<any>(null);

  const getTransactionsOdata = isOnFarm ? getOnFarmAccountTransactionsOdata : getAccountTransactionsOdata;

  const searchCriteriaOptions: FilterOption<TransactionFilterOption>[] = [
    {
      name: `${TABLE_HEADER_CONSTANTS.TRANSACTION_ID}`,
      value: { parameter: isOnFarm ? 'onFarmTransactionId' : 'inventoryTransactionId', isNumber: true },
    },
    { name: `${LABEL_CONSTANTS.NOTES}`, value: { parameter: 'notes' } },
    {
      name: `${TABLE_HEADER_CONSTANTS.ANIMAL_CODE}`,
      value: { parameter: isOnFarm ? 'onFarmSpecimen/onFarmAnimal/code' : 'specimen/animal/code' },
    },
    {
      name: `${TABLE_HEADER_CONSTANTS.ANIMAL_NAME}`,
      value: { parameter: isOnFarm ? 'onFarmSpecimen/onFarmAnimal/name' : 'specimen/animal/name' },
    },
    {
      name: `${TABLE_HEADER_CONSTANTS.LOT_NO}`,
      value: { parameter: isOnFarm ? 'onFarmSpecimenId' : 'specimenId', isNumber: true },
    },
  ];

  const handleSourceChange = (source: InventorySource) => {
    if (source === inventorySource) return;
    setInventorySource(source);
    setFilterPreset(defaultFilterPreset);
    setFilter('');
    setOffset(0);
    setTransactionsData([]);
    setListRefresh(0);
    setTransactionToCancel(undefined);
    hasAllResults.current = false;
  };

  const loadData = async () => {
    callNumber.current++;
    if (hasAllResults.current) {
      return;
    }
    const callNum = callNumber.current;
    try {
      setLoading(true);
      const { data: response } = await getTransactionsOdata(
        StandardParamsToODataParams({
          include: transactionInclude,
          filter: filter,
          sort: '^createdDatetime',
          limit: transactionFetchLimit,
          offset: offset,
        }),
      );
      if (offset === 0 && 'current' in ref) {
        ref.current?.scroll({ top: 0 });
      }
      if (callNum === callNumber.current) {
        const newTableData = offset > 0 ? [...transactionsData, ...response] : response;
        setTransactionsData(newTableData);
        setLoading(false);
        hasAllResults.current = response.length === 0;
      }
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  useEffect(() => {
    hasAllResults.current = false;
    setTransactionsData([]);
    if (offset === 0) {
      window.innerWidth <= 767 && loadData();
    } else {
      setOffset(0);
      if ('current' in ref) {
        ref.current?.scroll({ top: 0 });
      }
    }
  }, [filter, inventorySource, listRefresh]);

  useEffect(() => {
    window.innerWidth <= 767 && loadData();
  }, [offset]);

  useEffect(() => {
    window.addEventListener('touchmove', handleScroll);
    return () => window.removeEventListener('touchmove', handleScroll);
  }, []);

  useEffect(() => {
    isLoadingAdditionalPage.current = false;
  }, [transactionsData]);

  const loadMore = () => {
    setOffset(prevState => prevState + transactionFetchLimit);
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    const position = scrollHeight - scrollTop;
    const atBottom = position - clientHeight <= clientHeight;

    if (atBottom && !isLoadingAdditionalPage.current) {
      isLoadingAdditionalPage.current = true;
      loadMore();
    }
  };

  const navigateToDetail = (transactionId?: number) => {
    if (!transactionId) return;
    navigate(ROUTE_PATHS.APP_TRANSACTION_HISTORY_VIEW + transactionId, {
      state: { filterPreset: filterPreset, filter: filter, inventorySource },
    });
  };

  const getAnimalDisplay = (transaction: InventoryTransaction) => {
    const animalId = transaction.specimen?.animal?.animalId;
    const animalName = transaction.specimen?.animal?.name;
    if (animalId == null && !animalName) return '-';
    return `${animalId ?? ''} - ${animalName ?? ''}`;
  };

  const handleCancelTransaction = async () => {
    const onFarmTransactionId = transactionToCancel?.inventoryTransactionId;
    if (!onFarmTransactionId || cancellingTransaction) return;

    try {
      setCancellingTransaction(true);
      await cancelOnFarmTransaction(onFarmTransactionId);
      showToast.success(toastMessages.SUCCESSFULLY_CANCELED_TRANSACTION);
      setTransactionToCancel(undefined);
      setListRefresh(prev => prev + 1);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setCancellingTransaction(false);
    }
  };

  const getActionButtons = (transaction: InventoryTransaction) => {
    const actions = [
      {
        name: `${BUTTON_CONSTANTS.VIEW_DETAIL}`,
        action: () => navigateToDetail(transaction.inventoryTransactionId),
      },
    ];

    if (isOnFarm && transaction.status !== TransactionStatusEnum.Canceled) {
      actions.push({
        name: `${BUTTON_CONSTANTS.CANCEL}`,
        action: () => setTransactionToCancel(transaction),
      });
    }

    return actions;
  };

  const sourceToggle = REACT_APP_ON_FARM_ENABLED ? (
    <div className="transaction-source-toggle">
      {sourceOptions.map(option => (
        <button
          key={option.source}
          type="button"
          className={`transaction-source-button ${inventorySource === option.source ? 'active' : ''}`}
          onClick={() => handleSourceChange(option.source)}>
          {option.label}
        </button>
      ))}
    </div>
  ) : undefined;

  return (
    <div className="transaction-history">
      <TransactionsList
        key={inventorySource}
        header={NAVIGATION_LINK_CONSTANTS.TRANSACTION_HISTORY}
        searchCriteriaOptions={searchCriteriaOptions}
        setFilter={setFilter}
        setFilterPreset={setFilterPreset}
        filterPreset={filterPreset ?? defaultFilterPreset}
        isOData
        isTransactionHistoryPage={true}
        inventorySource={inventorySource}
        sourceToggle={sourceToggle}
        accountOwner={activeAccount}>
        <div className="desk-transaction-history">
          <PagedTable
            key={inventorySource}
            headers={[
              { displayName: `${TABLE_HEADER_CONSTANTS.TRANSACTION_ID}` },
              { displayName: `${TABLE_HEADER_CONSTANTS.ANIMAL}` },
              { displayName: `${TABLE_HEADER_CONSTANTS.TYPE}` },
              { displayName: `${TABLE_HEADER_CONSTANTS.CHANGED_QTY}` },
              { displayName: `${TABLE_HEADER_CONSTANTS.DATE}`, sortValue: 'CreatedDatetime' },
              { displayName: `${TABLE_HEADER_CONSTANTS.STATUS}` },
              { displayName: `${TABLE_HEADER_CONSTANTS.ACTION}` },
            ]}
            sortBy="^createdDatetime"
            getData={params => getTransactionsOdata(StandardParamsToODataParams(params))}
            filter={filter}
            include={transactionInclude}
            refresh={listRefresh}
            height={500}
            pageSize={10}
            scrollBufferThickness={100}
            buildRow={t => {
              return [
                t.inventoryTransactionId,
                getAnimalDisplay(t),
                TRANSACTION_TYPE_LABELS[t.transactionType as TransactionTypeEnum] ?? t.transactionType,
                t.changeQuantity,
                new Date(t.createdDatetime).toLocaleDateString(),
                t.status,

                <ActionMenu actionButtons={getActionButtons(t)} />,
              ];
            }}
          />
        </div>

        {/** Mobile View */}
        <div className="xs-transaction-history">
          {!loading && transactionsData.length === 0 ? (
            <div className="result-item">{`${LABEL_CONSTANTS.NO_RESULTS_FOUND}.`}</div>
          ) : (
            transactionsData &&
            transactionsData.map((transaction, index) => {
              return (
                <div className="card max-width" key={'transaction-' + transaction.inventoryTransactionId + index}>
                  <div className="transaction-history-card">
                    <div className="transaction-history-content">
                      <div className="transaction-info">
                        <div className="transaction-content">
                          <label>{`${TABLE_HEADER_CONSTANTS.TRANSACTION_ID}: `}</label>
                          <label>{`${transaction.inventoryTransactionId ?? '-'}`}</label>
                        </div>
                        <div className="transaction-content">
                          <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                          <label>{getAnimalDisplay(transaction)}</label>
                        </div>
                        <div className="transaction-content">
                          <label>{`${TABLE_HEADER_CONSTANTS.TYPE}: `}</label>
                          <label>{`${
                            TRANSACTION_TYPE_LABELS[transaction.transactionType as TransactionTypeEnum] ??
                            transaction.transactionType ??
                            '-'
                          }`}</label>
                        </div>
                      </div>
                      <div className="transaction-info">
                        <div className="transaction-content">
                          <label>{`${TABLE_HEADER_CONSTANTS.CHANGED_QTY}: `}</label>
                          <label>{`${transaction.changeQuantity ?? '-'}`}</label>
                        </div>
                        <div className="transaction-content">
                          <label>{`${TABLE_HEADER_CONSTANTS.DATE}: `}</label>
                          <label>{`${new Date(transaction.createdDatetime).toLocaleDateString() ?? '-'}`}</label>
                        </div>
                        <div className="transaction-content">
                          <label>{`${TABLE_HEADER_CONSTANTS.STATUS}: `}</label>
                          <label>{`${transaction.status ?? '-'}`}</label>
                        </div>
                      </div>
                    </div>
                    <div className="action-button">
                      <ActionMenu actionButtons={getActionButtons(transaction)} />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {loading && <Loader addedSpace loaderSize="small" />}
      </TransactionsList>
      <ConfirmModal
        modalHeader={TRANSACTION_CONSTANTS.CANCEL_TRANSACTION}
        modalBody={TRANSACTION_CONSTANTS.CANCEL_TRANSACTION_CONFIRM}
        isOpen={!!transactionToCancel}
        firstBtn={
          <button
            type="button"
            className="small inverted blue button"
            disabled={cancellingTransaction}
            onClick={() => setTransactionToCancel(undefined)}>
            {BUTTON_CONSTANTS.NO}
          </button>
        }
        secondBtn={
          <button type="button" className="small red button" disabled={cancellingTransaction} onClick={handleCancelTransaction}>
            {BUTTON_CONSTANTS.YES}
          </button>
        }
      />
    </div>
  );
};

export default TransactionHistory;
